import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Aug 2026 §10 — Contextual Ari Onboarding.
 * A small "first-time" tooltip Ari uses to explain unfamiliar surfaces
 * (Result History, Vault, Loop Scans, etc.). Shows exactly once per user
 * per feature key. Storage lives on the User document itself
 * (User.hints_seen) so we don't multiply schemas.
 *
 * Usage:
 *   <AriHint featureKey="vault-result-history"
 *            title="This is your Result History"
 *            body="Ari remembers what I've already found so I can show you..."
 *            onAskAri={(prompt) => openAri(prompt)} />
 */
export const AriHint = ({ featureKey, title, body, onAskAri }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/settings/hints-seen');
        const seen = (data?.hints_seen || []).includes(featureKey);
        if (!cancelled && !seen) setVisible(true);
      } catch {
        /* silent — hint just doesn't show */
      }
    })();
    return () => { cancelled = true; };
  }, [featureKey]);

  const dismiss = async () => {
    setVisible(false);
    try {
      await api.post('/settings/hints-seen', { key: featureKey });
    } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      data-testid={`ari-hint-${featureKey}`}
      className="relative rounded-2xl border border-[color:var(--zita-hairline,rgba(0,0,0,.06))] bg-ari/5 p-4 mb-4 flex items-start gap-3"
      style={{ backgroundColor: 'hsla(276, 60%, 55%, 0.06)' }}
    >
      <div className="w-8 h-8 rounded-full bg-ari flex items-center justify-center text-white shrink-0">
        <Sparkles size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-zita-espresso">{title}</div>
        <div className="text-sm text-zita-mocha mt-1 leading-relaxed">{body}</div>
        {onAskAri && (
          <button
            type="button"
            onClick={() => onAskAri(`Tell me more about ${title.toLowerCase()}`)}
            data-testid={`ari-hint-${featureKey}-ask`}
            className="mt-2 text-xs ari-accent hover:underline"
          >
            Ask Ari for more →
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid={`ari-hint-${featureKey}-dismiss`}
        className="p-1 rounded-full text-zita-mocha hover:bg-zita-sand shrink-0"
        aria-label="Dismiss hint"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default AriHint;
